import React, { Component, createRef } from "react";

// CSS
import "./Subscription.scss";

// Helper
import { setCookie, checkCookie } from "../../Helper/Cookies";
import { apiFetch, getCurrentUser } from "../../Helper/Api";

// Components
import Header from "../../components/Header/Header";
import { Panel, Confirm } from "../../components/UI/Overlays";
import { EmptyState, TableWait, StatTile, UserCell, Tip } from "../../components/UI/Feedback";
import { Toolbar } from "../../components/UI/Controls";
import AddSubscription from "./AddSubscription";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";

// Properties
const properties = require("../../properties.json");

class AllSubscriptions extends Component {
  constructor(props) {
    super(props);
    this.toast = createRef(null);
  }

  state = {
    globalFilter: null,

    // Whether the add-a-subscription panel is open. Nothing else about adding
    // one lives here: the panel mounts the very same AddSubscription the
    // /subscriptions/add route mounts, with its own state and its own submit.
    addOpen: false,

    // The rows are on their way. Until they arrive the table says so, instead
    // of claiming there are none.
    tableLoading: true,

    // The subscription the teacher asked to delete, held while the confirm is
    // on screen. The delete behind it is the same call it always was.
    doomed: null,
    deleting: false,

    // Teacher Identifier
    branchID: getCurrentUser()?.branchId,

    // Named Months
    namedMonths: [
      "يناير",
      "فبراير",
      "مارس",
      "إبريل",
      "مايو",
      "يونيو",
      "يوليو",
      "أغسطس",
      "سبتمبر",
      "أكتوبر",
      "نوفمبر",
      "ديسمبر",
    ],

    // Data
    data: [],
    stagesById: {},

    // What the table is actually showing right now. `data` is every row the
    // API returned -- the paginator only slices it for display, so counting
    // `data` is already correct across all pages. The search is the one thing
    // that makes the two differ: with a filter on, the table shows a subset
    // while `data` still holds everything. The table hands that subset over
    // through onValueChange, and the tiles read it so they can never claim a
    // total for rows that are not on screen. Null until the table first
    // reports, which is when it means "nothing filtered yet".
    shown: null,

    // Update Data
    resend: false,
  };

  getSubscriptions = () => {
    this.setState({ tableLoading: true });
    apiFetch("/students/enrollments")
      .then((fetchStudents) => {
        this.setState({
          data: fetchStudents,
          // the old filtered set belongs to rows that are gone; the table
          // reports a fresh one as soon as it has drawn these
          shown: null,
          tableLoading: false,
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ tableLoading: false });
      });
  };

  // A row carries the id of the stage it belongs to; this turns it into the
  // name the teacher knows it by. Same lookup the sibling pages already call.
  getStages = () => {
    apiFetch("/lookup/school-stages/by-teacher", { query: { teacherUserId: getCurrentUser()?.userId } })
      .then((stages) => {
        const stagesById = {};
        stages.forEach((s) => {
          stagesById[s.id] = s.name;
        });
        this.setState({ stagesById });
      })
      .catch((error) => console.error("Error:", error));
  };

  // The browser's own confirm box is gone; the question is asked in the
  // product's own confirm card instead (see render). Everything after the
  // teacher says yes -- the call, the toast, the refetch -- is unchanged.
  deleteSubscription = (id) => {
    var _this = this;

    this.setState({ deleting: true });

    apiFetch(`/students/enrollments/${id}`, { method: "DELETE" })
      .then(() => {
        this.setState({ resend: true, doomed: null, deleting: false });
        _this.toast.current.show({
          severity: "success",
          summary: "تمت العملية",
          detail: "تم الحذف بنجاح",
        });
        this.getSubscriptions();
      })
      .catch((error) => {
        console.error("Error:", error);

        this.setState({ doomed: null, deleting: false });

        _this.toast.current.show({
          severity: "error",
          summary: "لم تتم العملية",
          detail: "خطأ في الإتصال",
        });
      });
  };

  componentDidMount() {
    document.title = properties.site_name + " | كل إشتراكات الشهور";

    // Redirect To Sign In Page If The User Is Already Logged Out & Remove The Data If It Exists
    if (!checkCookie("name")) {
      window.localStorage.removeItem("currentTeacher");
      window.location.href = `/`;
    }

    this.getStages();
    this.getSubscriptions();
  }

  // componentDidUpdate(prevProps, prevState, snapshot) {
  //     if (prevState.resend !== this.state.resend) {
  //         this.getSubscriptions();

  //         this.setState({resend: false});
  //     }
  // }

  render() {
    const { tableLoading, doomed, deleting, data, shown } = this.state;

    // The rows the three numbers are about: whatever the table is showing,
    // which is every row until a search narrows it.
    const counted = shown || data;
    const filtering = counted.length !== data.length;

    // Adding a subscription is something you do on this page now, not a
    // second entry in the menu: the button opens the very same form the
    // /subscriptions/add route still mounts, in a panel over the list.
    const openAdd = () => this.setState({ addOpen: true });
    const closeAdd = () => {
      this.setState({ addOpen: false });
      // whatever was added while the panel was open belongs in the table
      this.getSubscriptions();
    };

    // The money, read as money: grouped digits, with the currency beside them
    // in a quieter voice. Same `value` the row always carried.
    const money = (v) => {
      const n = Number(v);
      if (!isFinite(n)) return "—";
      return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
    };

    // what the table adds up to, counted from the rows it is showing -- every
    // page of them, not just the one in view
    const total = counted.reduce((sum, row) => {
      const n = Number(row.value);
      return sum + (isFinite(n) ? n : 0);
    }, 0);

    // how many different students are on it -- a student can hold more than
    // one subscription, so the row count is not the head count
    const students = new Set(
      counted.map((row) => (row.studentProfileId === undefined || row.studentProfileId === null ? row.studentName : row.studentProfileId))
    ).size;

    const actionBodyTemplate = (rowData) => {
      return (
        <React.Fragment>
          {/* The pencil is still out of service here, exactly as it was --
              /subscriptions/edit/:id is not wired to this table yet. */}
          <Tip text="حذف">
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-danger"
              onClick={() => this.setState({ doomed: rowData })}
            />
          </Tip>
        </React.Fragment>
      );
    };

    // the subscription, as the teacher recognises it: whose it is, with the
    // mobile they reach them on under the name
    const studentBody = (row) => (
      <UserCell name={row.studentName || "—"} sub={row.studentMobile} />
    );

    // the period the response already names, with the stage it sits in under it
    const periodBody = (row) => (
      <span className="ui-stack">
        <b>{row.periodName || "—"}</b>
        <small>{this.state.stagesById[row.schoolStagesId] || "—"}</small>
      </span>
    );

    const valueBody = (row) => (
      <span className="ui-money">
        {money(row.value)}
        <small>ج.م</small>
      </span>
    );

    return (
      <>
        <Header
          pageName="كل إشتراكات الشهور"
          action={{ label: "إضافة إشتراك", icon: "pi pi-plus", onClick: openAdd }}
        />

        <Toast ref={this.toast}></Toast>

        {/* The form is not copied here -- the panel mounts AddSubscription
            itself, so its fields and its submit stay exactly as they are on
            /subscriptions/add, which still works as its own page. */}
        <Panel
          visible={this.state.addOpen}
          onHide={closeAdd}
          title="إضافة إشتراك"
          subtitle="اختار الطالب والفترة وحدد المبلغ، وهيظهر في الجدول بعد الحفظ"
        >
          {/* the panel closes itself on a successful save, and the table
              behind it goes and fetches the row that was just added */}
          {this.state.addOpen ? <AddSubscription inPanel onSaved={closeAdd} /> : null}
        </Panel>

        <Confirm
          visible={!!doomed}
          onHide={() => this.setState({ doomed: null })}
          name={doomed ? doomed.studentName : ""}
          note="الإشتراك هيتشال نهائياً، والطالب هيفقد وصوله للفترة دي."
          busy={deleting}
          onConfirm={() => this.deleteSubscription(doomed.id)}
        />

        <div className="content">
          <div className="container">
            <div className="card">
              <Card>
                {/* Three numbers the page already holds, read before the
                    rows: how many subscriptions, how many students behind
                    them, and what they add up to. They are counted over every
                    row the table holds, not the ten on screen -- and when a
                    search is on they follow it, and say so. */}
                <div className="ui-tiles">
                  <StatTile
                    icon="pi pi-calendar"
                    tone="teal"
                    label={filtering ? "إشتراكات نتيجة البحث" : "عدد الإشتراكات"}
                    value={tableLoading ? "…" : counted.length}
                  />
                  <StatTile
                    icon="pi pi-users"
                    tone="gold"
                    label={filtering ? "طلاب نتيجة البحث" : "عدد الطلاب"}
                    value={tableLoading ? "…" : students}
                  />
                  <StatTile
                    brand
                    icon="pi pi-wallet"
                    label={filtering ? "إجمالي نتيجة البحث" : "إجمالي المبالغ"}
                    value={tableLoading ? "…" : `${money(total)} ج.م`}
                  />
                </div>

                {/* search over the same globalFilter the table always read */}
                <Toolbar
                  value={this.state.globalFilter || ""}
                  onSearch={(v) => this.setState({ globalFilter: v })}
                  placeholder="بحث بالإسم أو رقم الموبايل"
                  count={
                    tableLoading
                      ? null
                      : filtering
                      ? `${counted.length} من ${data.length} إشتراك`
                      : `${data.length} إشتراك`
                  }
                />

                <DataTable
                  sortable
                  globalFilter={this.state.globalFilter}
                  className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                  value={data}
                  // the rows left after the search, handed over so the tiles
                  // above can count exactly what the table is showing
                  onValueChange={(rows) => this.setState({ shown: rows })}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 20, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                  emptyMessage={
                    tableLoading ? (
                      <TableWait />
                    ) : (
                      <EmptyState
                        title="لا يوجد إشتراكات"
                        hint="سجّل أول إشتراك لطالب في فترة من فتراتك"
                        icon="pi pi-calendar"
                        action={{ label: "إضافة إشتراك", onClick: openAdd }}
                      />
                    )
                  }
                >
                  {/* No per-column filter boxes: the search in the toolbar
                      already looks across the whole table, and the second row
                      of empty inputs under the headers was only taking room. */}
                  <Column
                    sortable
                    field="studentName"
                    body={studentBody}
                    header="الطالب"
                  ></Column>
                  <Column
                    sortable
                    field="studentMobile"
                    header="رقم الموبايل"
                  ></Column>
                  <Column
                    sortable
                    field="periodName"
                    body={periodBody}
                    header="الفترة"
                  ></Column>
                  <Column
                    sortable
                    field="value"
                    body={valueBody}
                    header="المبلغ"
                  ></Column>
                  <Column header="حذف" body={actionBodyTemplate}></Column>
                </DataTable>
              </Card>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default AllSubscriptions;
